/**
 * shared/galaxy.ts — CLIENT MIRROR of server/src/lib/galaxy.ts (the server
 * copy is authoritative; keep them byte-identical below the headers).
 * The client's lib/galaxy.ts re-exports this file (Space Wave 1: the server
 * computes the same systems, arrival points and station seats).
 */
/**
 * galaxy.ts — the GALACTIC layer above the sector: the Commons system plus
 * 100 prebuilt systems laid on a four-arm LOGARITHMIC SPIRAL (the density-
 * wave look: core bulge, arm jitter, per-star swirl), and one system per
 * PUBLIC user world (hashed into the halo between the arms).
 *
 * Pure + deterministic. Galactic coordinates are LIGHT-HOURS — a warp
 * tunnel crosses them in seconds; a manual course takes real DAYS.
 */

export interface StarSystem {
  id: string;                 // 'sys0' = Commons · 'sys<n>' prebuilt · 'w<worldId>' user world
  name: string;
  gx: number; gy: number; gz: number;   // light-hours
  starColor: number;
  starClass: 'dwarf' | 'main' | 'giant';
  planets: number;            // rendered planets in-system
  hasStation: boolean;
  tier: 1 | 2 | 3;            // pirate danger band
  worldId?: string;           // user systems: the landable world (stringified id)
}

function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
const h01 = (s: string): number => fnv(s) / 4294967296;

/** A WELL-MIXED unit hash for GEOMETRY (bearings, orbits, sizes, heights).
 *  FNV-1a alone is ARITHMETIC in its last character: the keys `…pa0` / `…pa1`
 *  differ by one bit before the final multiply, so their hashes differ by
 *  about the FNV prime — a 1.4° step in bearing — and every derived system's
 *  planets stood in ONE ray from the star with near-identical radii ("the
 *  planets were … all aligned in a row", 2026-09-09: 508 of 508 pairs within
 *  10°, 46 bodies inside their star). A murmur3 finalizer breaks the linearity.
 *  Names, colours, classes and tiers keep `fnv`: re-hashing them would rename
 *  every charted system on every player's map. */
function mix01(s: string): number {
  let h = fnv(s);
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const STAR_COLORS = [0xffd9a0, 0xfff4e0, 0xbfd8ff, 0xffb066, 0x9fe8ff, 0xffe9a0, 0xff9a7a];
const NAME_A = ['Vel', 'Kor', 'Ash', 'Ther', 'Mira', 'Sol', 'Ere', 'Cal', 'Nym', 'Ost', 'Bra', 'Quel', 'Dra', 'Fen', 'Hal', 'Ily', 'Jun', 'Kes', 'Lor', 'Mor'];
const NAME_B = ['adar', 'ithe', 'ovan', 'ara', 'este', 'ion', 'yra', 'anth', 'oria', 'une', 'ivar', 'emor', 'aris', 'okal', 'ethe'];
const NAME_C = ['', '', '', ' Prime', ' Reach', ' Drift', ' Verge', ' Deep', ' Gate', ' Expanse'];

function systemName(seed: string): string {
  return `${NAME_A[fnv(seed + 'a') % NAME_A.length]}${NAME_B[fnv(seed + 'b') % NAME_B.length]}${NAME_C[fnv(seed + 'c') % NAME_C.length]}`;
}

/** The Commons system's fixed seat on arm 0 (never at the core — the galaxy
 *  should stretch away in every direction from home). */
const COMMONS_SEAT = { gx: 620, gy: 8, gz: 140 };

let memo: StarSystem[] | null = null;

/** The 101 charted systems: Commons + 100 prebuilt on the spiral. */
export function prebuiltSystems(): StarSystem[] {
  if (memo) return memo;
  const out: StarSystem[] = [{
    id: 'sys0', name: 'The Commons Reach', ...COMMONS_SEAT,
    starColor: 0xfff4e0, starClass: 'main', planets: 5, hasStation: true, tier: 1,
  }];
  const ARMS = 4;
  const PITCH = 0.32;                    // log-spiral pitch (research: ~4 arms, e^(kθ))
  for (let i = 1; i <= 100; i++) {
    const seed = `sys${i}`;
    const arm = i % ARMS;
    // Core bulge for the first stretch, then the arms wind outward.
    const t = i / 100;
    const theta = t * 4.6 + (arm * Math.PI * 2) / ARMS + (h01(seed + 'j') - 0.5) * 0.35;
    const r = 90 * Math.exp(PITCH * t * 4.6) * (0.85 + h01(seed + 'r') * 0.3);
    const swirl = (h01(seed + 's') - 0.5) * 0.22;                // per-star swirl
    const gx = Math.cos(theta + swirl) * r;
    const gz = Math.sin(theta + swirl) * r;
    const gy = (h01(seed + 'y') - 0.5) * 90 * (1 - t * 0.6);     // the disc thins outward
    const tier = (t < 0.35 ? 1 : t < 0.7 ? 2 : 3) as 1 | 2 | 3;  // danger grows rimward
    out.push({
      id: seed,
      name: systemName(seed),
      gx, gy, gz,
      starColor: STAR_COLORS[fnv(seed + 'col') % STAR_COLORS.length],
      starClass: (['dwarf', 'main', 'main', 'giant'] as const)[fnv(seed + 'cls') % 4],
      planets: 2 + (fnv(seed + 'pl') % 4),
      hasStation: h01(seed + 'st') < 0.22,
      tier,
    });
  }
  memo = out;
  return out;
}

/** A PUBLIC user world's own system — hashed into the halo between arms. */
export function userSystem(worldId: string, name: string): StarSystem {
  const seed = `w${worldId}`;
  const a = h01(seed + 'ang') * Math.PI * 2;
  const r = 240 + h01(seed + 'rad') * 900;
  return {
    id: seed,
    name: `${name} System`,
    gx: Math.cos(a) * r,
    gy: (h01(seed + 'y') - 0.5) * 160,
    gz: Math.sin(a) * r,
    starColor: STAR_COLORS[fnv(seed + 'col') % STAR_COLORS.length],
    starClass: 'main',
    planets: 1 + (fnv(seed + 'pl') % 3),
    hasStation: false,
    tier: 1,
    worldId,
  };
}

export function galacticDistance(a: StarSystem, b: StarSystem): number {
  return Math.hypot(a.gx - b.gx, a.gy - b.gy, a.gz - b.gz);
}

/** Manual course time: one light-hour ≈ 40 real minutes at full burn —
 *  a neighboring system is hours; a cross-galaxy run is DAYS. */
export const COURSE_S_PER_LH = 2400;

// ── In-system content for non-Commons sectors (deterministic + light) ──
export interface SystemPlanet { name: string; radius: number; pos: [number, number, number]; texIdx: number; hue: number }
export interface SystemDen { pos: [number, number, number]; count: number; tier: 1 | 2 | 3; poiSlot: number }
export interface SystemField { pos: [number, number, number]; radius: number; count: number }
export interface SystemSector {
  star: { color: number; radius: number };
  planets: SystemPlanet[];
  fields: SystemField[];
  dens: SystemDen[];
  station: [number, number, number] | null;
}

// ── The LAYOUT of a derived system (2026-09-09 rework: "planets too close or
// all aligned in a row … make them more scattered") ──
/** The first orbit starts this many star radii out (+ PLANET_ORBIT_BASE_M);
 *  the arrival lane at 2.4 × the star radius stays clear of every planet. */
export const PLANET_ORBIT_FROM_STAR = 3.0;
export const PLANET_ORBIT_BASE_M = 150_000;
/** Orbit-to-orbit step (+ up to PLANET_ORBIT_JITTER_M), never less than the
 *  two surfaces plus PLANET_CLEARANCE_M. */
export const PLANET_ORBIT_STEP_M = 280_000;
export const PLANET_ORBIT_JITTER_M = 240_000;
export const PLANET_CLEARANCE_M = 160_000;
/** Bearings: the planets take EVEN slots around the star in a shuffled order,
 *  each jittered by ± a quarter slot — so any two are at least half a slot
 *  apart (5 planets: 36°, 4: 45°, 3: 60°, 2: 90°). */
export const PLANET_BEARING_JITTER = 0.25;
/** Vertical scatter (± m). */
export const PLANET_HEIGHT_M = 120_000;
/** Asteroid belts live INSIDE the first orbit (from 1.5 star radii + 60 km up
 *  to 120 km short of it) and keep ≥ 25° off the +X arrival bearing. */
export const FIELD_ORBIT_FROM_STAR = 1.5;
export const FIELD_ORBIT_BASE_M = 60_000;
export const FIELD_INSIDE_FIRST_ORBIT_M = 120_000;
export const FIELD_ARRIVAL_CLEAR_RAD = 25 * Math.PI / 180;

export interface PlanetSlot { r: number; a: number; y: number; radius: number }
/** Pure: the orbit radius, bearing, height and body radius of each planet. */
export function planetLayout(s: string, count: number, starRadius: number): PlanetSlot[] {
  const out: PlanetSlot[] = [];
  if (count <= 0) return out;
  // a shuffled assignment of the `count` even bearing slots
  const slots = Array.from({ length: count }, (_, i) => i).sort((i, j) => mix01(`${s}pslot${i}`) - mix01(`${s}pslot${j}`));
  const slotW = (Math.PI * 2) / count;
  const a0 = mix01(`${s}pa0`) * Math.PI * 2;
  let r = starRadius * PLANET_ORBIT_FROM_STAR + PLANET_ORBIT_BASE_M;
  let prevRadius = 0;
  for (let i = 0; i < count; i++) {
    const radius = 22_000 + mix01(`${s}ps${i}`) * 60_000;
    if (i > 0) {
      const step = PLANET_ORBIT_STEP_M + mix01(`${s}pr${i}`) * PLANET_ORBIT_JITTER_M;
      r += Math.max(step, prevRadius + radius + PLANET_CLEARANCE_M);
    }
    const a = a0 + slots[i] * slotW + (mix01(`${s}pj${i}`) - 0.5) * 2 * PLANET_BEARING_JITTER * slotW;
    const y = (mix01(`${s}py${i}`) - 0.5) * 2 * PLANET_HEIGHT_M;
    out.push({ r, a, y, radius });
    prevRadius = radius;
  }
  return out;
}

/** Pure: the belts' orbit radius and bearing — even slots inside the first
 *  planet orbit, the slot phase chosen so no belt sits on the arrival bearing. */
export function fieldLayout(s: string, count: number, starRadius: number, firstOrbit: number): Array<{ r: number; a: number }> {
  const out: Array<{ r: number; a: number }> = [];
  if (count <= 0) return out;
  const base = starRadius * FIELD_ORBIT_FROM_STAR + FIELD_ORBIT_BASE_M;
  const span = Math.max(40_000, firstOrbit - FIELD_INSIDE_FIRST_ORBIT_M - base);
  const slotW = (Math.PI * 2) / count;
  const clear = FIELD_ARRIVAL_CLEAR_RAD;
  // slot i lands in [i·slotW + clear, (i+1)·slotW − clear]: the phase range is
  // shrunk by the jitter amplitude, so jitter can never push a belt onto the lane
  const usable = Math.max(0.02, slotW - 2 * clear);
  const jitter = 0.15 * usable;
  const a0 = clear + jitter + mix01(`${s}fa0`) * (usable - 2 * jitter);
  for (let i = 0; i < count; i++) {
    out.push({ r: base + mix01(`${s}fr${i}`) * span, a: a0 + i * slotW + (mix01(`${s}fj${i}`) - 0.5) * 2 * jitter });
  }
  return out;
}

export function systemSector(sys: StarSystem): SystemSector {
  const s = sys.id;
  const starRadius = sys.starClass === 'giant' ? 220_000 : sys.starClass === 'main' ? 130_000 : 70_000;
  const planets: SystemPlanet[] = [];
  const layout = planetLayout(s, sys.planets, starRadius);
  for (let i = 0; i < sys.planets; i++) {
    const { r, a, y, radius } = layout[i];
    planets.push({
      name: `${sys.name} ${['I', 'II', 'III', 'IV', 'V'][i] ?? i + 1}`,
      radius,
      pos: [Math.cos(a) * r, y, Math.sin(a) * r],
      texIdx: Math.floor(mix01(`${s}pt${i}`) * 5),
      hue: mix01(`${s}ph${i}`),
    });
  }
  const fields: SystemField[] = [];
  const nf = 1 + (fnv(`${s}nf`) % 3);
  const firstOrbit = layout.length ? layout[0].r : starRadius * PLANET_ORBIT_FROM_STAR + PLANET_ORBIT_BASE_M;
  const flay = fieldLayout(s, nf, starRadius, firstOrbit);
  for (let i = 0; i < nf; i++) {
    const { r, a } = flay[i];
    fields.push({
      pos: [Math.cos(a) * r, (mix01(`${s}fy${i}`) - 0.5) * 60_000, Math.sin(a) * r],
      radius: 9_000 + mix01(`${s}fs${i}`) * 14_000,
      count: 26 + (fnv(`${s}fc${i}`) % 22),
    });
  }
  const dens: SystemDen[] = [];
  const nd = 1 + (fnv(`${s}ndc`) % 2);
  for (let i = 0; i < nd; i++) {
    const f = fields[i % fields.length];
    dens.push({
      pos: [f.pos[0] + 6_000, f.pos[1], f.pos[2] - 4_000],
      count: 3 + (fnv(`${s}dc${i}`) % 4),
      tier: sys.tier,
      // Bounty keys ride the SERVER regex (poi0-99): map each den onto the
      // tier-matched poi band so kills pay/loot/progress like home dens.
      poiSlot: sys.tier === 3 ? [9, 18, 20, 27][i % 4] : sys.tier === 1 ? 12 + (i % 4) : [1, 2, 3, 5][i % 4],
    });
  }
  // The station rides just outside the star (it used to sit 90 km from the
  // centre of a 130–220 km star), off the arrival lane.
  const stationR = starRadius * 1.4 + 50_000;
  const stationA = FIELD_ARRIVAL_CLEAR_RAD + mix01(`${s}sta`) * (Math.PI * 2 - 2 * FIELD_ARRIVAL_CLEAR_RAD);
  const station = sys.hasStation
    ? [Math.cos(stationA) * stationR, 8_000, Math.sin(stationA) * stationR] as [number, number, number]
    : null;
  return { star: { color: sys.starColor, radius: starRadius }, planets, fields, dens, station };
}

// ── Per-system nav targets + chart POIs (the "space local map must match the
// system you are in" wave, 2026-08-24). navPoints()/POIS are the COMMONS
// sector only; every other system derives its targets from systemSector so
// N-cycling, the M chart, autopilot and the radar all agree on THIS system.
export type SystemNavKind = 'planet' | 'station' | 'asteroids' | 'fortress';
export interface SystemNavPoint {
  name: string;
  pos: [number, number, number];
  arrive: number;
  kind: SystemNavKind;
  tier: 1 | 2 | 3;
  /** Field/den volume radius (chart rendering). */
  radius: number;
}
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

/** Nav-cyclable targets of a non-Commons system: station, belts, dens, planets. */
export function systemNavPoints(sys: StarSystem): SystemNavPoint[] {
  const sec = systemSector(sys);
  const out: SystemNavPoint[] = [];
  if (sec.station) out.push({ name: `${sys.name} Station`, pos: sec.station, arrive: 9_000, kind: 'station', tier: 1, radius: 4_000 });
  sec.fields.forEach((f, i) => out.push({
    name: `${sys.name} Belt ${ROMAN[i] ?? i + 1}`, pos: f.pos, arrive: Math.max(9_000, f.radius * 1.6), kind: 'asteroids', tier: sys.tier, radius: f.radius,
  }));
  sec.dens.forEach((d, i) => out.push({
    name: `${sys.name} Den ${ROMAN[i] ?? i + 1}`, pos: d.pos, arrive: 9_000, kind: 'fortress', tier: d.tier, radius: 5_000,
  }));
  for (const pl of sec.planets) out.push({ name: pl.name, pos: pl.pos, arrive: pl.radius * 2.2, kind: 'planet', tier: 1, radius: pl.radius });
  return out;
}
